import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, map, firstValueFrom } from 'rxjs';
import { take, takeUntil, filter } from 'rxjs/operators';

import { AppState } from '@store/app.state';
import { UserConfigActions } from '@store/actions/user-config.actions';
import { SettingsActions } from '@store/actions/settings.actions';
import { PaymentActions } from '@store/actions/payment.actions';
import { selectLeftSideContainerWidth, selectRightSideContainerWidth, selectFramesHeight } from '@store/selectors/user-config.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { selectHasPaid, selectHasPaidStatus } from '@store/selectors/payment.selectors';
import { AppTheme, Settings, CANVAS_DIM_MIN, CANVAS_DIM_MAX, defaultSetting, resolutionFromCustomSize } from '@models/settings.model';
import { PixelFrame } from '@models/frame.model';
import { ModalService } from '@app/services/modal.service';
import { PersistenceService } from '@app/services/persistence.service';
import { MenuSettings } from './layout/menu/settings/menu.settings';

import { ColorPalette } from './layout/color-palette/color-palette';
import { Tools } from './layout/tools/tools';
import { Editor } from './layout/editor/editor';
import { StartupDialog, StartupResult } from './layout/startup/startup-dialog';
import { FileMenu } from './layout/file-menu/file-menu';
import { EditMenu } from './layout/edit-menu/edit-menu';
import { FramesTimeline } from './layout/frames-timeline/frames-timeline';
import { Frames } from './layout/frames/frames';
import { RightSideBar } from './layout/right-side-bar/right-side-bar';
import { CollabPanelComponent } from './layout/collab-panel/collab-panel';
import { FrameActions } from '@store/actions/frame.actions';
import { selectAllFrames, selectCels, selectLayers, selectActiveLayerId, selectCurrentFrameId, selectFrameState } from '@store/selectors/frame.selectors';
import { createBlankFrame } from '@models/frame.model';
import { createBlankSpriteLayer } from '@models/layer.model';
import { CollaborationService } from '@app/services/collaboration.service';
import { AuthService, AuthState } from '@app/services/auth.service';
import { AuthModal } from '@app/shared/auth-modal/auth-modal';
import { UsernamePicker } from '@app/shared/username-picker/username-picker';
import { generateUUID } from '@app/lib/utility/helper';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, AsyncPipe, RouterOutlet, ColorPalette, Tools, Editor, FramesTimeline, Frames, RightSideBar, FileMenu, EditMenu, StartupDialog, CollabPanelComponent, AuthModal, UsernamePicker],
  templateUrl: './app.html',
})
export class App implements OnInit, OnDestroy {
  leftW$: Observable<number>;
  rightW$: Observable<number>;
  framesH$: Observable<number>;
  currentTheme$: Observable<AppTheme>;
  gridCols$: Observable<string>;
  authState$!: Observable<AuthState>;

  // Frames bottom height (resizable)
  framesH = 200;
  private minFramesH = 90;
  private maxFramesH = 400;

  // Column resize state
  private isResizingLeft = false;
  private isResizingRight = false;
  // Row resize state (frames height)
  private isResizingRow = false;
  private rowResizeStartY = 0;
  private rowResizeStartH = 0;

  @ViewChild('editorRef') editorRef!: Editor;
  @ViewChild('collabPanel') collabPanel!: CollabPanelComponent;
  @ViewChild('fileMenuRef') fileMenuRef!: FileMenu;
  private destroy$ = new Subject<void>();

  constructor(private store: Store<AppState>, private modalService: ModalService, private router: Router, private persistence: PersistenceService, public collabSvc: CollaborationService, public auth: AuthService) {
    this.leftW$ = this.store.select(selectLeftSideContainerWidth);
    this.rightW$ = this.store.select(selectRightSideContainerWidth);
    this.framesH$ = this.store.select(selectFramesHeight);
    this.framesH$.subscribe(h => { if (h) this.framesH = h; });
    this.currentTheme$ = this.store.select(selectSettings).pipe(map(s => s.theme ?? 'pxlvd'));
    // Mirror data-theme onto document.body so canvas JS (getComputedStyle(document.body)) reads correct vars
    this.currentTheme$.subscribe(t => document.body.setAttribute('data-theme', t));
    this.projectName$ = this.store.select(selectSettings).pipe(map(s => s.projectName ?? 'Untitled Project'));
    this.authState$ = this.auth.state$;
    this.gridCols$ = combineLatest([this.leftW$, this.rightW$]).pipe(
      map(([l, r]) => `${l}px 4px 1fr 4px ${r}px`)
    );
    this.isDocsRoute = this.isStaticRoute(window.location.pathname);
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: any) => {
      this.isDocsRoute = this.isStaticRoute(e.urlAfterRedirects ?? '');
    });

    // Open login modal when any component (e.g. pricing page) requests it
    this.auth.loginRequest$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (!this.auth.isAuthenticated) {
        this.showLoginModal = true;
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeyDown(e: KeyboardEvent): void {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      this.triggerSave();
    }
  }

  /** Returns an error message if the project cannot be saved safely, or null if OK. */
  private validateCanvasForSave(settings: Settings, frames: PixelFrame[]): string | null {
    const w = settings.Resolution?.width ?? 0;
    const h = settings.Resolution?.height ?? 0;
    if (!frames?.length) {
      return 'There is nothing to save yet. Create or load a project first.';
    }
    if (w < CANVAS_DIM_MIN || h < CANVAS_DIM_MIN || w > CANVAS_DIM_MAX || h > CANVAS_DIM_MAX) {
      return 'Canvas resolution is missing or invalid. Use File → New Project or Project Settings to choose a canvas size, then try again.';
    }
    const f0 = frames[0];
    const fw = f0?.pixels?.[0]?.length ?? 0;
    const fh = f0?.pixels?.length ?? 0;
    if (fw !== w || fh !== h) {
      return 'Canvas size does not match your frames. Adjust resolution in Project Settings or start a new project with a matching size.';
    }
    for (const f of frames) {
      const pw = f.pixels[0]?.length ?? 0;
      const ph = f.pixels.length;
      if (pw !== w || ph !== h) {
        return 'Some frames are a different size than the canvas. Fix this before saving.';
      }
    }
    return null;
  }

  triggerSave(): void {
    if (this.collabSvc.isInRoom$.value && !this.collabSvc.can('saveCanvas')) {
      alert('Admin has disabled member canvas snapshot saves in this room.');
      return;
    }
    this.store.select(selectSettings).pipe(take(1)).subscribe(async settings => {
      const frames        = await firstValueFrom(this.store.select(selectAllFrames).pipe(take(1)));
      const layers        = await firstValueFrom(this.store.select(selectLayers).pipe(take(1)));
      const cels          = await firstValueFrom(this.store.select(selectCels).pipe(take(1)));
      const activeLayerId = await firstValueFrom(this.store.select(selectActiveLayerId).pipe(take(1)));
      const currentFrameId = await firstValueFrom(this.store.select(selectCurrentFrameId).pipe(take(1)));
      // Read history so it's saved immediately on Ctrl+S (not deferred 500ms to debounce).
      const frameState    = await firstValueFrom(this.store.select(selectFrameState).pipe(take(1)));
      const canvasErr = this.validateCanvasForSave(settings, frames);
      if (canvasErr) {
        alert(canvasErr);
        return;
      }
      const projectId = settings.currentProjectId ?? generateUUID();
      const projectName = settings.projectName ?? 'Untitled Project';
      const isNewProject = !settings.currentProjectId;
      // Only check for duplicates if this is a brand-new project (no existing ID)
      if (isNewProject) {
        const existing = await this.persistence.listProjects();
        const duplicate = existing.find(p => p.name.toLowerCase() === projectName.toLowerCase());
        if (duplicate) {
          // Re-use the existing project's ID to overwrite rather than duplicate
          this.store.dispatch(SettingsActions.updateSettings({ settings: { currentProjectId: duplicate.id } as any }));
          await this.persistence.saveProject(duplicate.id, projectName, frames, currentFrameId, layers, cels, activeLayerId, frameState.past, frameState.future);
          await this.persistence.touchProject(duplicate.id);
          localStorage.setItem('pxlvd-last-project', duplicate.id);
          this.store.dispatch(FrameActions.saveFrames());
          this.showSaveToast();
          return;
        }
      }
      await this.persistence.saveProject(projectId, projectName, frames, currentFrameId, layers, cels, activeLayerId, frameState.past, frameState.future);
      await this.persistence.touchProject(projectId);
      this.store.dispatch(SettingsActions.updateSettings({ settings: { currentProjectId: projectId } as any }));
      localStorage.setItem('pxlvd-last-project', projectId);
      this.store.dispatch(FrameActions.saveFrames());
      this.showSaveToast();
    });
  }

  showStartup = true;  // show immediately to prevent canvas flash
  showLoginModal = false;
  showUsernamePicker = false;
  private _pendingAction: string | null = null;
  collabFrames: { id: string; pixels: string[][]; width: number; height: number }[] = [];
  collabCels: Record<string, string[][]> = {};
  collabLayers: any[] = [];
  collabActiveLayerId: string | null = null;
  saveToast = false;
  isDocsRoute = false;
  projectName$!: Observable<string>;
  showSaveBeforeCollab = false;
  private _saveBeforeCollabResolver: ((saved: boolean) => void) | null = null;

  // Resolution mismatch dialog
  showResolutionMismatch = false;
  resMismatch: { roomWidth: number; roomHeight: number; localWidth: number; localHeight: number } | null = null;

  showSaveToast(): void {
    this.saveToast = true;
    setTimeout(() => this.saveToast = false, 2000);
  }

  async handleStartup(result: StartupResult): Promise<void> {
    this.store.dispatch(SettingsActions.updateSettings({ settings: { projectName: result.projectName } as any }));
    if (result.action === 'load' && result.loadProjectId) {
      // Load from project-specific storage (not the legacy single-key 'db-store' that
      // FrameActions.loadFrames() uses).  After a canvasWipe$ the legacy key holds a
      // blank frame, so using loadFrames() here would silently replace the selected
      // project's pixels with blank ones - causing an empty canvas in the editor and
      // an empty room for any collaborators the admin then invites.
      const id = result.loadProjectId;
      const data = await this.persistence.loadProject(id);
      if (data?.frames?.length) {
        const migrated = this.persistence.migrateFrameData(data);
        this.store.dispatch(FrameActions.restoreFrameState({
          frames: migrated.frames,
          layers: migrated.layers,
          cels: migrated.cels,
          activeLayerId: migrated.activeLayerId,
          currentFrameId: migrated.currentFrameId,
          past: migrated.past,
          future: migrated.future
        }));
        // Restore canvas resolution from the frame pixel dimensions.
        // Resolution is not stored in a separate settings key - it lives in the
        // frame pixels themselves (each row/col count is the canvas width/height).
        const firstFrame = migrated.frames[0];
        const w = firstFrame?.pixels?.[0]?.length ?? 54;
        const h = firstFrame?.pixels?.length ?? 96;
        this.store.dispatch(SettingsActions.updateSettings({
          settings: { currentProjectId: id, Resolution: resolutionFromCustomSize(w, h) } as any
        }));
        await this.persistence.touchProject(id);
      } else {
        this.store.dispatch(SettingsActions.updateSettings({ settings: { currentProjectId: id } as any }));
      }
      localStorage.setItem('pxlvd-last-project', id);
    } else if (result.action === 'load') {
      // Legacy fallback: no project ID supplied - load from legacy single-key storage.
      this.store.dispatch(FrameActions.loadFrames());
    } else {
      const { width, height } = result.resolution;
      const frame = createBlankFrame(width, height);
      // Bug-fix: always create a fresh default layer so that layer names from
      // any previously-loaded project don't bleed into the new one.
      // setFrames only resets `frames` when `layers` is omitted (it falls through
      // to `state.layers`), so we must supply explicit defaults every time.
      const defaultLayer = createBlankSpriteLayer('Layer 1');
      const projectId = generateUUID();
      this.store.dispatch(FrameActions.setFrames({
        frames: [frame],
        layers: [defaultLayer],
        cels: {},
        activeLayerId: defaultLayer.id,
      }));
      this.store.dispatch(SettingsActions.updateSettings({ settings: { Resolution: result.resolution, projectName: result.projectName, currentProjectId: projectId } as any }));
      await this.persistence.saveProject(projectId, result.projectName, [frame], frame.id, [defaultLayer], {}, defaultLayer.id);
      await this.persistence.touchProject(projectId);
      localStorage.setItem('pxlvd-last-project', projectId);
    }
    this.showStartup = false;
  }

  handleStartupImport(): void {
    this.showStartup = false;
    // Trigger file import via hidden input
    setTimeout(() => {
      const inp = document.createElement('input');
      inp.type = 'file'; inp.accept = '.pxlvd,.json';
      inp.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) { this.showStartup = true; return; }
        const reader = new FileReader();
        reader.onload = async (ev) => {
          try {
            const data = JSON.parse(ev.target?.result as string);
            if (data.frames) {
              const migrated = this.persistence.migrateFrameData({
                frames:        data.frames,
                layers:        data.layers        ?? undefined,
                cels:          data.cels          ?? undefined,
                activeLayerId: data.activeLayerId ?? undefined,
                currentFrameId: data.currentFrameId ?? null,
                past: [], future: [],
              });
              const first = migrated.frames[0];
              const importedWidth  = first?.pixels?.[0]?.length ?? 54;
              const importedHeight = first?.pixels?.length       ?? 96;
              const projectName = (data.name?.trim?.() || 'Imported Project') as string;
              // Re-use an existing project with the same name instead of creating a duplicate
              const existingProjects = await this.persistence.listProjects();
              const nameMatch = existingProjects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
              const projectId = nameMatch?.id ?? generateUUID();
              this.store.dispatch(FrameActions.restoreFrameState({
                frames:        migrated.frames,
                layers:        migrated.layers,
                cels:          migrated.cels,
                activeLayerId: migrated.activeLayerId,
                currentFrameId: migrated.currentFrameId ?? first?.id ?? null,
                past: [], future: [],
              }));
              this.store.dispatch(SettingsActions.updateSettings({
                settings: {
                  projectName,
                  currentProjectId: projectId,
                  Resolution: {
                    width: importedWidth,
                    height: importedHeight,
                    label: `${importedWidth}×${importedHeight}`,
                    aspectRatio: importedHeight ? `${importedWidth}:${importedHeight}` : '1:1',
                    exportWidth: importedWidth * 20,
                    exportHeight: importedHeight * 20,
                  }
                } as any
              }));
              await this.persistence.saveProject(
                projectId, projectName,
                migrated.frames, migrated.currentFrameId ?? first?.id ?? null,
                migrated.layers, migrated.cels, migrated.activeLayerId,
              );
              // Mirror into legacy frame storage as a fallback for next boot.
              await this.persistence.saveFrames({
                frames:        migrated.frames,
                currentFrameId: migrated.currentFrameId ?? first?.id ?? null,
                layers:        migrated.layers,
                cels:          migrated.cels,
                activeLayerId: migrated.activeLayerId,
                past: [], future: [],
              });
              await this.persistence.touchProject(projectId);
              localStorage.setItem('pxlvd-last-project', projectId);
            }
            else { this.showStartup = true; }
          } catch { this.showStartup = true; }
        };
        reader.readAsText(file);
      };
      inp.click();
    }, 100);
  }

  ngOnInit(): void {
    this.store.dispatch(UserConfigActions.loadUserConfig());
    this.store.dispatch(SettingsActions.loadSettings());

    // Keep collab frames in sync with the store (include canvas dimensions)
    combineLatest([
      this.store.select(selectAllFrames),
      this.store.select(selectSettings)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([frames, settings]) => {
      const w = settings.Resolution.width;
      const h = settings.Resolution.height;
      this.collabFrames = frames.map(f => ({ id: f.id, pixels: f.pixels, width: w, height: h }));
    });

    // Keep collab cels in sync so createRoom can include initial cel data
    this.store.select(selectCels).pipe(takeUntil(this.destroy$)).subscribe(cels => {
      this.collabCels = cels;
    });

    // Keep collab layers + activeLayerId in sync so createRoom can seed yLayers
    // with the admin's initial layer structure.  Without this, joining members see
    // only their own initial "Layer 1" until the admin modifies a layer.
    this.store.select(selectLayers).pipe(takeUntil(this.destroy$)).subscribe(layers => {
      this.collabLayers = layers;
    });
    this.store.select(selectActiveLayerId).pipe(takeUntil(this.destroy$)).subscribe(id => {
      this.collabActiveLayerId = id;
    });

    // Canvas wipe - fires when user is kicked or leaves a collab room
    this.collabSvc.canvasWipe$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Clear the canvas and remove the project reference so the user
      // cannot access the collab canvas after leaving/being kicked
      localStorage.removeItem('pxlvd-last-project');
      this.store.dispatch(FrameActions.setFrames({ frames: [createBlankFrame(54, 96)] }));
      this.store.dispatch(SettingsActions.updateSettings({
        settings: {
          projectName: 'Untitled Project',
          currentProjectId: undefined,
          Resolution: defaultSetting.Resolution,
        } as any
      }));
      this.showStartup = true;
    });

    // When a user is approved into a collab room, the editor must be visible so the
    // remote canvas sync can render. Hide startup/show editor immediately on approval.
    this.collabSvc.isInRoom$.pipe(
      filter(v => v === true),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.showStartup = false;
    });

    // Resolution mismatch on join: silently adopt the room's resolution for all users.
    // Resolution is locked once in a collab room - it's set at room creation time.
    // take(1) prevents a double-adopt if both synced and peers events fire before
    // the store update propagates - without it _adoptResolution could dispatch twice
    // and overwrite a local edit that arrived between the two dispatches.
    this.collabSvc.resolutionMismatch$.pipe(take(1), takeUntil(this.destroy$)).subscribe(mismatch => {
      this._adoptResolution(mismatch.roomWidth, mismatch.roomHeight);
    });

    // Wait for auth to initialize - then check username and project state
    let _lastUserId: string | null | undefined = undefined; // undefined = not yet seen
    this.auth.state$.pipe(
      filter(s => s.initialized),
      takeUntil(this.destroy$)
    ).subscribe(async (state) => {
      const currentUserId = state.user?.id ?? null;

      // First emission after app boot - just init project state normally
      if (_lastUserId === undefined) {
        _lastUserId = currentUserId;
        await this._initProjectState();
        if (state.user) {
          const has = await this.auth.hasUsername();
          if (!has) this.showUsernamePicker = true;
        }
        return;
      }

      // User changed (logout, or switched account)
      if (currentUserId !== _lastUserId) {
        _lastUserId = currentUserId;
        // Leave any active collab room
        if (this.collabSvc.isInRoom$.value) this.collabSvc.leaveRoom();
        // Clear the persisted project reference so we don't load another user's canvas
        localStorage.removeItem('pxlvd-last-project');
        // Reset the canvas store to a blank state
        this.store.dispatch(FrameActions.setFrames({ frames: [createBlankFrame(54, 96)] }));
        this.store.dispatch(SettingsActions.updateSettings({ settings: { projectName: 'Untitled Project', currentProjectId: undefined } as any }));

        if (state.user) {
          // Logged in as a new user - re-init their project state
          await this._initProjectState();
          const has = await this.auth.hasUsername();
          if (!has) this.showUsernamePicker = true;
        } else {
          // Logged out - show startup dialog
          this.showStartup = true;
        }
      }
    });
  }

  private async _initProjectState(): Promise<void> {
    const hasCollabInvite = /\/room\/[A-Z0-9]+/i.test(window.location.pathname)
      || /\/room[=%3D]+[A-Z0-9]+/i.test(window.location.pathname) // legacy /room=XXXX or /room%3DXXXX
      || !!sessionStorage.getItem('pxlvd_pending_room'); // pending invite survives URL changes
    if (hasCollabInvite) {
      // Exception: the room creator refreshing the page needs their last project loaded
      // so the collab panel can call createRoom() with the correct canvas frames.
      // Non-creator users (members, non-creator admins, fresh invite links) skip this
      // and proceed with a blank canvas - the room sync will fill it in after joining.
      const last = this.collabSvc.getLastRoom();
      const isAdminCreatorRefresh = last?.isCreator === true && last?.role === 'admin';
      if (!isAdminCreatorRefresh) {
        this.showStartup = false;
        return;
      }
      // Admin creator falls through to load their last project below
    }
    const lastId = localStorage.getItem('pxlvd-last-project');
    if (lastId) {
      const data = await this.persistence.loadProject(lastId);
      if (data?.frames?.length) {
        const migrated = this.persistence.migrateFrameData(data);
        this.store.dispatch(FrameActions.restoreFrameState({
          frames: migrated.frames,
          layers: migrated.layers,
          cels: migrated.cels,
          activeLayerId: migrated.activeLayerId,
          currentFrameId: migrated.currentFrameId,
          past: migrated.past,
          future: migrated.future,
        }));
        // Restore canvas resolution from frame pixel dimensions
        const firstFrameInit = migrated.frames[0];
        const initW = firstFrameInit?.pixels?.[0]?.length ?? 54;
        const initH = firstFrameInit?.pixels?.length ?? 96;
        await this.persistence.touchProject(lastId);
        const projects = await this.persistence.listProjects();
        const entry = projects.find(p => p.id === lastId);
        this.store.dispatch(SettingsActions.updateSettings({
          settings: {
            projectName: entry?.name,
            currentProjectId: lastId,
            Resolution: resolutionFromCustomSize(initW, initH),
          } as any
        }));
        this.showStartup = false;
        return;
      }
    }
    const legacy = await this.persistence.loadFrames();
    if (legacy?.frames?.length) {
      const migratedLegacy = this.persistence.migrateFrameData(legacy);
      this.store.dispatch(FrameActions.restoreFrameState({
        frames: migratedLegacy.frames,
        layers: migratedLegacy.layers,
        cels: migratedLegacy.cels,
        activeLayerId: migratedLegacy.activeLayerId,
        currentFrameId: migratedLegacy.currentFrameId,
        past: migratedLegacy.past,
        future: migratedLegacy.future,
      }));
      // Restore canvas resolution from frame pixel dimensions
      const legacyFirstFrame = migratedLegacy.frames[0];
      const legacyW = legacyFirstFrame?.pixels?.[0]?.length ?? 54;
      const legacyH = legacyFirstFrame?.pixels?.length ?? 96;
      this.store.dispatch(SettingsActions.updateSettings({
        settings: { Resolution: resolutionFromCustomSize(legacyW, legacyH) } as any
      }));
      this.showStartup = false;
      return;
    }
    this.showStartup = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isStaticRoute(path: string): boolean {
    return path.startsWith('/docs')
      || path.startsWith('/home')
      || path.startsWith('/login')
      || path.startsWith('/reset-password')
      || path.startsWith('/payment');
  }

  /** Intercepts the collab panel opening - prompts to save if there are unsaved frames */
  async onCollabPanelOpening(): Promise<void> {
    const hasUnsaved = await this._hasUnsavedWork();
    if (hasUnsaved) {
      this.showSaveBeforeCollab = true;
      // Wait for user decision
      const saved = await new Promise<boolean>(resolve => {
        this._saveBeforeCollabResolver = resolve;
      });
      this.showSaveBeforeCollab = false;
      if (!saved) {
        // User chose to discard - just proceed
        this.collabPanel.proceedOpen();
        return;
      }
      // Auto-save with a guaranteed unique project ID
      await this._saveCurrentProjectUnique();
      this.collabPanel.proceedOpen();
    } else {
      this.collabPanel.proceedOpen();
    }
  }

  onSaveBeforeCollabConfirm(): void {
    this._saveBeforeCollabResolver?.(true);
    this._saveBeforeCollabResolver = null;
  }

  onSaveBeforeCollabDiscard(): void {
    this._saveBeforeCollabResolver?.(false);
    this._saveBeforeCollabResolver = null;
  }

  onSaveBeforeCollabCancel(): void {
    this.showSaveBeforeCollab = false;
    this._saveBeforeCollabResolver = null;
    // Don't proceed - user cancelled opening collab entirely
  }

  /** User chose to adopt the room's resolution */
  onResolutionAdoptRoom(): void {
    if (this.resMismatch) {
      const { roomWidth, roomHeight } = this.resMismatch;
      this.store.select(selectSettings).pipe(take(1)).subscribe(settings => {
        const newResolution = {
          ...settings.Resolution,
          width: roomWidth,
          height: roomHeight,
          exportWidth: roomWidth * 20,
          exportHeight: roomHeight * 20
        };
        this.store.dispatch(SettingsActions.updateSettings({
          settings: { ...settings, Resolution: newResolution } as any
        }));
      });
    }
    this.showResolutionMismatch = false;
    this.resMismatch = null;
  }

  /** User chose to keep their local resolution (may cause display issues) */
  onResolutionKeepLocal(): void {
    this.showResolutionMismatch = false;
    this.resMismatch = null;
  }

  /** Silently adopt a new resolution and resize all local frames to match */
  private _adoptResolution(width: number, height: number): void {
    this.store.select(selectSettings).pipe(take(1)).subscribe(settings => {
      // Update settings
      const newResolution = {
        ...settings.Resolution,
        width, height,
        exportWidth: width * 20, exportHeight: height * 20
      };
      this.store.dispatch(SettingsActions.updateSettings({
        settings: { ...settings, Resolution: newResolution } as any
      }));
      // Resize all existing frames and cels to the new dimensions via the reducer.
      // resizeCanvas handles both PixelFrame pixels and all sparse cels in one atomic op.
      this.store.dispatch(FrameActions.resizeCanvas({ width, height }));
    });
  }

  private async _hasUnsavedWork(): Promise<boolean> {
    return new Promise(resolve => {
      this.store.select(selectAllFrames).pipe(take(1)).subscribe(frames => {
        // Has unsaved work if there are non-blank frames
        const hasPixels = frames.some(f =>
          f.pixels.some(row => row.some(px => px !== '#00000000' && px !== 'transparent'))
        );
        resolve(hasPixels);
      });
    });
  }

  /** Save current project before opening collab, reusing an existing entry if the name matches. */
  private async _saveCurrentProjectUnique(): Promise<void> {
    const settings      = await firstValueFrom(this.store.select(selectSettings).pipe(take(1)));
    const frames        = await firstValueFrom(this.store.select(selectAllFrames).pipe(take(1)));
    const layers        = await firstValueFrom(this.store.select(selectLayers).pipe(take(1)));
    const cels          = await firstValueFrom(this.store.select(selectCels).pipe(take(1)));
    const activeLayerId = await firstValueFrom(this.store.select(selectActiveLayerId).pipe(take(1)));
    const currentFrameId = await firstValueFrom(this.store.select(selectCurrentFrameId).pipe(take(1)));
    const frameState    = await firstValueFrom(this.store.select(selectFrameState).pipe(take(1)));
    const projectName   = settings.projectName ?? 'Untitled Project';
    // Reuse the current project ID if set, otherwise check for a same-named project
    // to avoid creating a duplicate entry in the recent list every time collab is opened.
    let projectId = settings.currentProjectId;
    if (!projectId) {
      const existing  = await this.persistence.listProjects();
      const nameMatch = existing.find(p => p.name.toLowerCase() === projectName.toLowerCase());
      projectId = nameMatch?.id ?? generateUUID();
    }
    await this.persistence.saveProject(projectId, projectName, frames, currentFrameId, layers, cels, activeLayerId, frameState.past, frameState.future);
    this.store.dispatch(SettingsActions.updateSettings({
      settings: { currentProjectId: projectId } as any
    }));
    localStorage.setItem('pxlvd-last-project', projectId);
    this.showSaveToast();
  }

  requireAuth(): void {
    if (!this.auth.isAuthenticated) {
      this.showLoginModal = true;
    }
  }

  async onPaymentRequired(event: { feature: 'collab' | 'export'; proceed: () => void }): Promise<void> {
    if (!this.auth.isAuthenticated) {
      this.showLoginModal = true;
      return;
    }
    const hasPaid = await this._ensureHasPaidForFeatureAccess();
    if (hasPaid) {
      event.proceed();
      return;
    }
    await this.router.navigate(['/home/pricing']);
  }

  private async _ensureHasPaidForFeatureAccess(): Promise<boolean> {
    const status = await firstValueFrom(this.store.select(selectHasPaidStatus).pipe(take(1)));
    if (status === 'idle') this.store.dispatch(PaymentActions.checkHasPaid());
    if (status !== 'checked') {
      await firstValueFrom(this.store.select(selectHasPaidStatus).pipe(
        filter(s => s === 'checked'),
        take(1)
      ));
    }
    return firstValueFrom(this.store.select(selectHasPaid).pipe(take(1)));
  }

  /** Called when the collab panel emits authRequired (e.g. opening panel or arriving via invite link) */
  onCollabAuthRequired(): void {
    if (!this.auth.isAuthenticated) {
      this._pendingAction = 'collabJoin';
      this.showLoginModal = true;
    }
  }

  onAuthenticated(): void {
    this.showLoginModal = false;
    this._checkUsernameAfterAuth();
  }

  private async _checkUsernameAfterAuth(): Promise<void> {
    const has = await this.auth.hasUsername();
    if (!has) {
      this.showUsernamePicker = true;
      // don't clear _pendingAction - resume after username is set
      return;
    }
    this._resumePendingAction();
  }

  onUsernameSet(username: string): void {
    this.showUsernamePicker = false;
    // Update collab user with chosen username
    const existingUser = this.collabSvc.localUser;
    if (existingUser) {
      this.collabSvc.saveLocalUser({ ...existingUser, username });
    }
    this._resumePendingAction();
  }

  private _resumePendingAction(): void {
    const action = this._pendingAction;
    this._pendingAction = null;

    // If login was triggered from a home/docs page (e.g. pricing), stay there -
    // the component reacts to auth.state$ on its own. Don't touch showStartup.
    if (this.isDocsRoute) return;

    if (action === 'projectManager') {
      this.showStartup = true;
    } else if (action === 'collabJoin') {
      // Re-run the collab panel init now that user is authed - it will pick up joinRoomId
      if (this.collabPanel) {
        this.collabPanel.resumeAfterAuth();
      }
    }
  }

  onOpenProjectManager(): void {
    if (!this.auth.isAuthenticated) {
      this._pendingAction = 'projectManager';
      this.showLoginModal = true;
    } else {
      this.showStartup = true;
    }
  }

  setTheme(theme: AppTheme): void {
    localStorage.setItem('pxlvd-theme', theme);
    document.body.setAttribute('data-theme', theme);
    this.store.select(selectSettings).pipe(take(1)).subscribe(settings => {
      this.store.dispatch(SettingsActions.updateSettings({ settings: { ...settings, theme } }));
    });
  }

  /** Called when a .pxlvd file is imported via the file menu - dismisses the startup dialog. */
  onFileImported(): void {
    this.showStartup = false;
  }

  openSettings(): void {
    if (this.collabSvc.role$.value === 'spectator') return;
    this.modalService.open({ title: 'Settings', componentContent: MenuSettings, saveButtonText: '↓ Save Settings', confirmButtonText: 'Close' });
  }

  openImport(): void {
    this.modalService.open({
      title: 'Import',
      textContent: 'Use the Import section in the right panel to upload and pixelate an image.',
      confirmButtonText: 'Got It'
    });
  }

  openDownload(): void {
    this.modalService.open({
      title: 'Export',
      textContent: 'Open File -> Export to download WebM, MP4, spritesheets, PNG sequences, and project files.',
      confirmButtonText: 'OK'
    });
  }

  openHelp(): void {
    this.modalService.open({
      title: 'Canvas Controls',
      textContent: '🖱 Left Click+Drag - Paint\n🖱 Middle Mouse+Drag - Pan\n🖱 Scroll - Zoom\n⌨ Space+Drag - Pan\n⌨ F - Fit to screen\n⌨ B - Draw  E - Erase  G - Fill  I - Pick',
      confirmButtonText: 'Got It'
    });
  }

  showClearDataConfirm = false;
  get isSpectatorMode(): boolean { return this.collabSvc.role$.value === 'spectator'; }

  openClearData(): void {
    this.showClearDataConfirm = true;
  }

  async confirmClearData(): Promise<void> {
    this.showClearDataConfirm = false;

    // Sign out of Supabase session first so auth tokens are cleared cleanly
    try { await this.auth.signOut(); } catch {}

    // Wipe IndexedDB databases used by the app.
    // deleteDatabase() is callback-based (not Promise), so wrap it explicitly.
    await this._clearIndexedDbData();

    // Wipe all localStorage keys
    try { localStorage.clear(); } catch {}

    // Wipe sessionStorage too
    try { sessionStorage.clear(); } catch {}

    // Force a full page reload so all in-memory state is gone
    window.location.href = '/';
  }

  private async _clearIndexedDbData(): Promise<void> {
    const known = ['belaku-db', 'pxlvd-store'];
    const names = new Set<string>(known);
    try {
      const listFn = (indexedDB as IDBFactory & {
        databases?: () => Promise<Array<{ name?: string }>>;
      }).databases;
      if (typeof listFn === 'function') {
        const dbs = await listFn.call(indexedDB);
        for (const db of dbs) if (db?.name) names.add(db.name);
      }
    } catch {}

    await Promise.all(Array.from(names).map(name => this._deleteIndexedDb(name)));
  }

  private _deleteIndexedDb(name: string): Promise<void> {
    return new Promise(resolve => {
      try {
        const req = indexedDB.deleteDatabase(name);
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
        req.onblocked = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  // ── Column resizers ──
  onColResizeStart(event: MouseEvent, side: 'left' | 'right'): void {
    event.preventDefault();
    side === 'left' ? (this.isResizingLeft = true) : (this.isResizingRight = true);
  }

  // ── Row resizer (frames height) ──
  onRowResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.isResizingRow = true;
    this.rowResizeStartY = event.clientY;
    this.rowResizeStartH = this.framesH;
  }

  onGlobalMouseMove(event: MouseEvent): void {
    if (this.isResizingLeft) {
      const w = Math.max(160, Math.min(420, event.clientX));
      this.store.dispatch(UserConfigActions.updateLeftSideContainerWidth({ width: w }));
    }
    if (this.isResizingRight) {
      const w = Math.max(160, Math.min(420, document.body.clientWidth - event.clientX));
      this.store.dispatch(UserConfigActions.updateRightSideContainerWidth({ width: w }));
    }
    if (this.isResizingRow) {
      const delta = this.rowResizeStartY - event.clientY; // drag up = bigger frames
      this.framesH = Math.max(this.minFramesH, Math.min(this.maxFramesH, this.rowResizeStartH + delta));
    }
  }

  onGlobalMouseUp(): void {
    if (this.isResizingRow) {
      this.store.dispatch(UserConfigActions.updateFramesHeight({ height: this.framesH }));
    }
    this.isResizingLeft = false;
    this.isResizingRight = false;
    this.isResizingRow = false;
  }
}